import { useMemo } from "react";
import ReactMarkdown from "react-markdown";
import { contentPages } from "@/content/content-pages";
import "./changelog.css";

type ChangelogItem = {
  body: string;
  category: string;
};

type ChangelogRelease = {
  date: string;
  displayDate: string;
  slug: string;
  items: ChangelogItem[];
};

const CATEGORY_RULES: Array<{ label: string; pattern: RegExp }> = [
  { label: "Accounts", pattern: /account|sign.?in|sign.?up|sign.?out|profile|auth|verification|password/i },
  { label: "Request Flow", pattern: /request|missing race/i },
  { label: "Search", pattern: /search|records/i },
  { label: "Email", pattern: /email|notification/i },
  { label: "Content", pattern: /content|legal|blog|changelog|privacy/i },
  { label: "Navigation", pattern: /navigation|footer|links|nav/i },
  { label: "Experience", pattern: /interaction|visual|consistency|app shell/i },
];

function inferCategory(body: string): string {
  return (
    CATEGORY_RULES.find(({ pattern }) => pattern.test(body))?.label ??
    "Update"
  );
}

function formatReleaseDate(value: string): string {
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;

  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(date);
}

function releaseSlug(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

function parseChangelog(markdown: string): ChangelogRelease[] {
  const releases: ChangelogRelease[] = [];
  let current: ChangelogRelease | null = null;

  for (const rawLine of markdown.split("\n")) {
    const line = rawLine.trim();
    if (!line) continue;

    if (line.startsWith("## ")) {
      const date = line.replace(/^##\s+/, "").trim();
      current = {
        date,
        displayDate: formatReleaseDate(date),
        slug: releaseSlug(date),
        items: [],
      };
      releases.push(current);
      continue;
    }

    if (current && line.startsWith("- ")) {
      const body = line.replace(/^-\s+/, "").trim();
      current.items.push({
        body,
        category: inferCategory(body),
      });
    }
  }

  return releases;
}

export default function ChangelogPage() {
  const releases = useMemo(
    () => parseChangelog(contentPages.changelog.markdown),
    [],
  );
  const latest = releases[0];

  return (
    <section className="changelog-page d8-reveal revealed d8-reveal-delay-1">
      <div className="changelog-hero">
        <div className="changelog-hero-kicker">Product updates</div>
        <h1 className="changelog-title">Changelog</h1>
        <p className="changelog-subtitle">
          What changed across search, race coverage, data quality, and the
          records experience.
        </p>
      </div>

      <div className="changelog-layout">
        <article className="changelog-feed" aria-label="Product changelog">
          {releases.map((release, index) => (
            <section
              key={release.slug}
              id={release.slug}
              className="changelog-release"
            >
              <div className="changelog-release-marker" aria-hidden="true" />
              <header className="changelog-release-header">
                <div>
                  <p className="changelog-release-label">Release</p>
                  <h2 className="changelog-release-date">
                    {release.displayDate}
                  </h2>
                </div>
                {index === 0 ? (
                  <span className="changelog-latest-badge">Latest</span>
                ) : null}
              </header>

              <div className="changelog-release-body">
                {release.items.map((item) => (
                  <div key={`${release.slug}-${item.body}`} className="changelog-item">
                    <span className="changelog-category">{item.category}</span>
                    <div className="changelog-item-copy">
                      <ReactMarkdown
                        components={{
                          p: ({ children }) => <>{children}</>,
                        }}
                      >
                        {item.body}
                      </ReactMarkdown>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          ))}
        </article>

        <aside className="changelog-rail" aria-label="Changelog dates">
          <div className="changelog-rail-inner">
            <p className="changelog-rail-title">On this page</p>
            <nav className="changelog-date-nav">
              {releases.map((release) => (
                <a key={release.slug} href={`#${release.slug}`}>
                  <span>{release.displayDate}</span>
                  {release === latest ? (
                    <span className="changelog-nav-dot" aria-label="Latest" />
                  ) : null}
                </a>
              ))}
            </nav>
          </div>
        </aside>
      </div>
    </section>
  );
}
